/**
 * Account API client: the credential embed, the auth hash and GET /memberships in one place.
 *
 * The reservation popup and the PDP membership block both call the account API, and both need the
 * same `sha256(customerId + email + salt)` contract against the same `#ProductMembershipManagement`
 * embed — a second copy of either drifts the moment the salt or the envelope changes.
 */
(function () {
  'use strict';

  if (window.A9AccountApi) return;

  const CREDENTIALS_EMBED_ID = 'ProductMembershipManagement';
  /** A stalled request would otherwise hold the membership sentence back for the whole session. */
  const REQUEST_TIMEOUT_MS = 8000;

  /** Credentials main-product.liquid embeds, or null for a guest / an incomplete embed. */
  function readCredentials() {
    const element = document.getElementById(CREDENTIALS_EMBED_ID);
    if (!element) return null;

    try {
      const data = JSON.parse(element.textContent);
      if (!data?.endpoint || !data?.salt || !data?.customer_id || !data?.customer_email) return null;
      return {
        endpoint: data.endpoint,
        salt: data.salt,
        customerId: data.customer_id,
        customerEmail: data.customer_email
      };
    } catch (error) {
      return null;
    }
  }

  /** `window.sha256` when the popup has loaded it, Web Crypto otherwise — the PDP loads neither. */
  async function authHash(credentials) {
    const creds = credentials || readCredentials();
    if (!creds) return null;

    const message = String(creds.customerId) + creds.customerEmail + creds.salt;
    try {
      if (typeof window.sha256 === 'function') return await window.sha256(message);
      if (!window.crypto?.subtle) return null;

      const digest = await window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(message));
      return Array.from(new Uint8Array(digest)).map((byte) => byte.toString(16).padStart(2, '0')).join('');
    } catch (error) {
      console.error('Error generating account auth hash:', error);
      return null;
    }
  }

  /**
   * Membership rows for the logged-in customer, or null when the API could not be asked or did not
   * answer. A wrong salt answers 401/403 — degrade to null so the page keeps working on the metafield.
   */
  async function fetchMemberships() {
    const credentials = readCredentials();
    if (!credentials) return null;

    try {
      const hash = await authHash(credentials);
      if (!hash) return null;

      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
      try {
        const response = await fetch(
          `${credentials.endpoint}/memberships?id=${encodeURIComponent(credentials.customerId)}`,
          { headers: { 'Content-Type': 'application/json', Authorization: hash }, signal: controller.signal }
        );
        if (!response.ok) return null;

        const payload = await response.json();
        return Array.isArray(payload?.data) ? payload.data : null;
      } finally {
        clearTimeout(timeout);
      }
    } catch (error) {
      console.warn('Account membership lookup failed — keeping the metafield answer', error);
      return null;
    }
  }

  window.A9AccountApi = { readCredentials, authHash, fetchMemberships };
})();
